import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui';
import { 
  Volume2, 
  VolumeX,
  Sparkles,
  ChevronDown,
  ChevronUp,
  ArrowRight
} from 'lucide-react';
import { trackExamplesViewed } from '@/services/analyticsService';

// Example video data - easy to extend
interface VideoExample {
  id: string;
  videoUrl: string;
  promptKey: string; // Translation key for the prompt
  isVertical?: boolean; // Optional: true for 9:16 vertical videos
}

// Add your examples here - just add more objects to extend
// Videos from OpenAI Sora and https://fal.ai/models/fal-ai/veo3/examples
const VIDEO_EXAMPLES: VideoExample[] = [
  // ========== SORA EXAMPLES ==========
  {
    id: 'figure-skater-cat',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final/golden/6eda9a57-5d6d-4890-90ee-61f89e999719/triple_axel.mp4',
    promptKey: 'examples.prompts.figureSkaterCat',
  },
  {
    id: 'paddleboard-backflip',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final/golden/6eda9a57-5d6d-4890-90ee-61f89e999719/paddleboard.mp4',
    promptKey: 'examples.prompts.paddleboardBackflip',
  },
  {
    id: 'two-horses',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final/golden/6eda9a57-5d6d-4890-90ee-61f89e999719/20250929_1800_New%20Video_simple_compose_01k6c25b5jf6d950z61a8sgs9g.mp4',
    promptKey: 'examples.prompts.twoHorses',
  },
  {
    id: 'anime-fireworks',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final/golden/6eda9a57-5d6d-4890-90ee-61f89e999719/anime_fireworks%20(1).mp4',
    promptKey: 'examples.prompts.animeFireworks',
  },
  {
    id: 'astronaut-dog',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final/golden/6eda9a57-5d6d-4890-90ee-61f89e999719/20250922_1557_an%20astronaut%20golden%20retriever%20named%20_Sora_%20levitates%20around%20an%20intergalactic%20pup-themed%20space%20statio_simple_compose_01k5sta9hmegr9axrpbty5sxva%20(1).mp4',
    promptKey: 'examples.prompts.astronautDog',
  },
  {
    id: 'guy-backflip',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/20250924_2154_New%20Video_simple_compose_01k5zkjk6cewmbdna01qtg293g.mp4',
    promptKey: 'examples.prompts.guyBackflip',
  },
  {
    id: 'gymnast-balance-beam',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/20250924_2249_New%20Video_simple_compose_01k5zpppg5ev8swe27vvj9en08.mp4',
    promptKey: 'examples.prompts.gymnastBalanceBeam',
  },
  {
    id: 'cannonball-dive',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/20250924_2256_New%20Video_simple_compose_01k5zq3n4dfzyv7bje2smkq1tm.mp4',
    promptKey: 'examples.prompts.cannonballDive',
  },
  {
    id: 'horse-on-horse',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/20250929_0337_New%20Video_simple_compose_01k6agr8ctey29mze208t29w38%20(1).mp4',
    promptKey: 'examples.prompts.horseOnHorse',
  },
  {
    id: 'volleyball',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/20250929_0404_New%20Video_simple_compose_01k6aja0rpe0yt9cge2743tb8r.mp4',
    promptKey: 'examples.prompts.volleyball',
  },
  {
    id: 'skateboarder-kickflip',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/Copy%20of%2020250925_2005_New%20Video_simple_compose_01k61zpe8yfshs06fb6e768v88.mp4',
    promptKey: 'examples.prompts.skateboarderKickflip',
  },
  {
    id: 'dalmatian-obstacle',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/dalmation_gymnastics.mp4',
    promptKey: 'examples.prompts.dalmatianObstacle',
  },
  {
    id: 'vikings-war',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/cinematic/cinematic/20250911_2156_Vikings%20Go%20To%20War%20%E2%80%94%20North%20Sea%20Launch%20(10.0s,%20Winter%20cool%20daylight%20_%20early%20medieval)__Clinker-built%20o_simple_compose_01k4y4g0gbe15r9b2j32bhdyme%20(2).mp4',
    promptKey: 'examples.prompts.vikingsWar',
  },
  {
    id: 'anime-awakening',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/cinematic/cinematic/anime_awakening_v2%20(1).mp4',
    promptKey: 'examples.prompts.animeAwakening',
  },
  {
    id: 'ghibli-dog',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/cinematic/cinematic/ghibli_dog.mp4',
    promptKey: 'examples.prompts.ghibliDog',
  },
  {
    id: 'anime-academia',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/cinematic/cinematic/openai_academia4.mp4',
    promptKey: 'examples.prompts.animeAcademia',
  },
  {
    id: 'mountain-explorers',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/audio/20250917_1550_Two%20mountain%20explorers%20in%20bright%20technical%20shells,%20ice%20crusted%20faces,%20eyes%20narrowed%20with%20urgency%20sho_simple_compose_01k5cxxqm3f3as63704h22e6vp%20(1).mp4',
    promptKey: 'examples.prompts.mountainExplorers',
  },
  {
    id: 'lanterns-festival',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/audio/20250924_2217_New%20Video_simple_compose_01k5zmvgtneqvspy56ark3tt7v%20(1).mp4',
    promptKey: 'examples.prompts.lanternsFestival',
  },
  {
    id: 'martial-artist-koi',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/audio/20250929_0337_New%20Video_simple_compose_01k6ags3ztfg8a2xme8kbghq3h%20(1).mp4',
    promptKey: 'examples.prompts.martialArtistKoi',
  },
  {
    id: 'scuba-diver',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/audio/20250929_2232_New%20Video_simple_compose_01k6chq0nrewsbqkxhnvp77k2y.mp4',
    promptKey: 'examples.prompts.scubaDiver',
  },
  {
    id: 'professor-multilingual',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/audio/20250929_2243_New%20Video_simple_compose_01k6cjbphffbwvktsd774srwab.mp4',
    promptKey: 'examples.prompts.professorMultilingual',
  },
  {
    id: 'claymation-orchestra',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/audio/Copy%20of%2020250918_1431_a%20claymation%20conductor%20conducts%20a%20claymation%20orchestra_simple_compose_01k5fbtf2cfrtvxgmj5fv44r02%20(2).mp4',
    promptKey: 'examples.prompts.claymationOrchestra',
  },
  // Vertical cameo videos (9:16 aspect ratio)
  {
    id: 'bigfoot-kind',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/cameo/19700121_0020_68c9063fee24819184d91bc7abea06dc%20(1).mp4',
    promptKey: 'examples.prompts.bigfootKind',
    isVertical: true,
  },
  {
    id: 'flying-person',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/cameo/19700121_0032_68d48b0e63908191ba57a4551f199a35%20(1).mp4',
    promptKey: 'examples.prompts.flyingPerson',
    isVertical: true,
  },
  {
    id: 'arm-wrestling',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/cameo/20250924_1143_New%20Video_simple_compose_01k5ygjgtdfqhrg1rs2eb5n0h8%20(1).mp4',
    promptKey: 'examples.prompts.armWrestling',
    isVertical: true,
  },
  {
    id: 'ostrich-hat',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/cameo/20250928_1515_New%20Video_simple_compose_01k69692dqex9agdpqcakjfm7v%20(1).mp4',
    promptKey: 'examples.prompts.ostrichHat',
    isVertical: true,
  },
  {
    id: 'trumpet-zebras',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/cameo/20250928_1610_New%20Video_simple_compose_01k699enp0e9etjhwz3rhhzwxv%20(1).mp4',
    promptKey: 'examples.prompts.trumpetZebras',
    isVertical: true,
  },
  {
    id: 'bucking-ostrich',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/cameo/20250929_0028_New%20Video_simple_compose_01k6a5z8azethbkf6x0bgsfn6w%20(1).mp4',
    promptKey: 'examples.prompts.buckingOstrich',
    isVertical: true,
  },
  {
    id: 'superhero-dog',
    videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final2/cameo/20250929_0201_New%20Video_simple_compose_01k6ab93gefpvs03j6gxsn3jga%20(1).mp4',
    promptKey: 'examples.prompts.superheroDog',
    isVertical: true,
  },
  // ========== VEO 3 (FAL) EXAMPLES ==========
  {
    id: 'medieval-battlefield',
    videoUrl: 'https://v3.fal.media/files/penguin/D-wlVxx1E8BPr2AG9cxhb_output.mp4',
    promptKey: 'examples.prompts.medievalBattlefield',
  },
  {
    id: 'ancient-library',
    videoUrl: 'https://v3.fal.media/files/zebra/bW6Jj6jeerM5Y4jwo49tN_output.mp4',
    promptKey: 'examples.prompts.ancientLibrary',
  },
  {
    id: 'bosphorus-yacht',
    videoUrl: 'https://v3.fal.media/files/penguin/ZOkKJXXKEDJ9fsNAeMNkE_output.mp4',
    promptKey: 'examples.prompts.bosphorusYacht',
  },
  {
    id: 'batman-alfred',
    videoUrl: 'https://v3.fal.media/files/tiger/HyOP0PV6ZzA0mEm14riNj_output.mp4',
    promptKey: 'examples.prompts.batmanAlfred',
  },
  {
    id: '80s-singer',
    videoUrl: 'https://v3.fal.media/files/koala/2Ig5BpHjmOK_Ct42ueAYC_output.mp4',
    promptKey: 'examples.prompts.eightiesSinger',
  },
  {
    id: 'daniel-lions-den',
    videoUrl: 'https://v3.fal.media/files/lion/NfyXdCFBvAzRsMe2R1Fb8_output.mp4',
    promptKey: 'examples.prompts.danielLionsDen',
  },
  {
    id: 'police-car',
    videoUrl: 'https://v3.fal.media/files/penguin/OHd4AcqqpxXCXCc7JZ8rl_output.mp4',
    promptKey: 'examples.prompts.policeCar',
  },
  {
    id: 'cyberpunk-skater',
    videoUrl: 'https://v3.fal.media/files/panda/476McAVlSQMqM7lXptiMc_output.mp4',
    promptKey: 'examples.prompts.cyberpunkSkater',
  },
];

interface VideoCardProps {
  example: VideoExample;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

function VideoCard({ example, isExpanded, onToggleExpand }: VideoCardProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  // Use Intersection Observer to detect when video is visible
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.3, // Start playing when 30% of video is visible
        rootMargin: '50px', // Start loading slightly before it's visible
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Play/pause video based on visibility
  useEffect(() => {
    if (!videoRef.current) return;

    if (isVisible) {
      videoRef.current.play().catch(() => {
        // Autoplay may be blocked by browser, video will still show first frame
      });
    } else {
      videoRef.current.pause();
    }
  }, [isVisible]);

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const prompt = t(example.promptKey);
  // Show first 150 characters when collapsed
  const truncatedPrompt = prompt.length > 150 ? prompt.slice(0, 150) + '...' : prompt;
  const shouldShowExpandButton = prompt.length > 150;

  return (
    <div 
      ref={containerRef}
      className={cn(
        "group relative rounded-2xl overflow-hidden bg-surface border border-glass-border hover:border-primary/30 transition-all duration-300 hover:shadow-[0_0_30px_rgba(139,92,246,0.15)]",
        example.isVertical && "max-w-[280px] mx-auto"
      )}
    >
      {/* Video Container */}
      <div className={cn(
        "relative overflow-hidden bg-black",
        example.isVertical ? "aspect-[9/16]" : "aspect-video"
      )}>
        <video
          ref={videoRef}
          src={example.videoUrl}
          className="w-full h-full object-cover"
          loop
          muted={isMuted}
          playsInline
          preload="metadata"
        />

        {/* Volume Control - Always visible (sticky) */}
        <button
          onClick={toggleMute}
          className={cn(
            'absolute bottom-3 right-3 p-2.5 rounded-full',
            'backdrop-blur-md transition-all duration-200',
            'hover:scale-110',
            isMuted 
              ? 'bg-black/60 text-white/80 hover:bg-black/80 hover:text-white' 
              : 'bg-primary/80 text-white hover:bg-primary'
          )}
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5" />
          ) : (
            <Volume2 className="w-5 h-5" />
          )}
        </button>

      </div>

      {/* Prompt Section */}
      <div className="p-4">
        <div className="flex items-start gap-2 mb-2">
          <span className="text-xs font-semibold text-primary uppercase tracking-wide">
            {t('generator.prompt')}
          </span>
        </div>
        
        <p className="text-sm text-text-secondary leading-relaxed">
          {isExpanded ? prompt : truncatedPrompt}
        </p>

        {shouldShowExpandButton && (
          <button
            onClick={onToggleExpand}
            className="mt-2 flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-light transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                {t('generator.showLess')}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                {t('generator.showMore')}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export function ExamplesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [showStickyCta, setShowStickyCta] = useState(false);
  const secondVideoRef = useRef<HTMLDivElement>(null);

  // Track examples page view
  useEffect(() => {
    trackExamplesViewed();
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Show sticky CTA when 2nd video enters the viewport (mobile only)
  useEffect(() => {
    const handleScroll = () => {
      const secondVideo = secondVideoRef.current;
      if (!secondVideo) return;

      const rect = secondVideo.getBoundingClientRect();
      // Show CTA when the top of the 2nd video enters the viewport
      setShowStickyCta(rect.top < window.innerHeight);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Separate horizontal and vertical videos for better layout
  const horizontalVideos = VIDEO_EXAMPLES.filter(v => !v.isVertical);
  const verticalVideos = VIDEO_EXAMPLES.filter(v => v.isVertical);

  return (
    <>
      <div className="max-w-6xl mx-auto animate-fade-in pb-28 md:pb-24">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">{t('examples.badge')}</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
            {t('examples.title')}
          </h1>
          
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {t('examples.subtitle')}
          </p>
        </div>

        {/* Horizontal Video Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          {horizontalVideos.map((example, index) => (
            <div key={example.id} ref={index === 1 ? secondVideoRef : undefined}>
              <VideoCard
                example={example}
                isExpanded={expandedCards.has(example.id)}
                onToggleExpand={() => toggleExpand(example.id)}
              />
            </div>
          ))}
        </div>

        {/* Vertical Videos Grid */}
        {verticalVideos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-10">
            {verticalVideos.map((example) => (
              <VideoCard
                key={example.id}
                example={example}
                isExpanded={expandedCards.has(example.id)}
                onToggleExpand={() => toggleExpand(example.id)}
              />
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="text-center">
          <div className="inline-flex flex-col items-center p-8 rounded-2xl bg-gradient-to-br from-primary/10 via-surface to-accent/10 border border-glass-border">
            <h2 className="text-xl md:text-2xl font-bold text-text-primary mb-3">
              {t('examples.ctaTitle')}
            </h2>
            <p className="text-text-secondary mb-6 max-w-md">
              {t('examples.ctaSubtitle')}
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate('/')}
              className="group"
            >
              {t('examples.ctaButton')}
              <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Sticky CTA Bar - Liquid Glass Design */}
      <div className={cn(
        "fixed bottom-20 md:bottom-6 left-0 right-0 z-50 md:left-64 px-4 pointer-events-none transition-all duration-300",
        // On mobile: show only after scrolling past 2nd video, on desktop: always visible
        showStickyCta ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 md:opacity-100 md:translate-y-0"
      )}>
        <div className="max-w-lg mx-auto pointer-events-auto">
          {/* Liquid Glass Pill */}
          <button
            onClick={() => navigate('/')}
            className="group w-full flex items-center justify-between gap-3 px-5 py-3.5 md:py-4 rounded-full bg-black/40 backdrop-blur-xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.1)] hover:bg-black/50 hover:border-white/20 hover:shadow-[0_8px_40px_rgba(139,92,246,0.2),inset_0_1px_0_rgba(255,255,255,0.15)] transition-all duration-300"
          >
            {/* Text */}
            <span className="text-white/80 text-sm md:text-base font-medium truncate">
              {t('examples.stickyCta.button')}
            </span>
            
            {/* Arrow Button */}
            <div className="flex-shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/20 transition-all duration-300">
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-white/90 transition-transform group-hover:translate-x-0.5" />
            </div>
          </button>
        </div>
      </div>
    </>
  );
}
